/**
 * AttachmentManager — reusable file-attachment widget for Client Profile
 * card items (Past Performances, Certifications, Key Personnel,
 * Winning Proposals).
 *
 * v3.11.26 — initial drop with multi-file upload via the unified
 * /api/knowledge-base/attachments endpoint family. The widget renders
 * a paperclip-with-count button that opens a popover listing existing
 * attachments + a multi-file input. Each attachment row supports
 * download + delete.
 *
 * Linkage to Pursuit Manage Documents (pull / push / win→library) is
 * intentionally out-of-scope for this release — see ROADMAP.md.
 */
import { useEffect, useState } from 'react';
import { Paperclip, Upload, Download, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 10;
const MAX_BYTES = 50 * 1024 * 1024;

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function AttachmentManager({ itemType, itemId, onCountChange }) {
  const { authToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  // Fetch the list lazily — first time the popover is opened, plus once
  // up front so the count badge is accurate before any interaction.
  const fetchAttachments = async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/${itemType}/${itemId}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        onCountChange?.((data.items || []).length);
      }
    } catch {
      // non-blocking; keep last known list
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, itemType]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    if (files.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per upload`);
      return;
    }
    const oversize = files.find((f) => f.size > MAX_BYTES);
    if (oversize) {
      toast.error(`${oversize.name} exceeds 50 MB limit`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/${itemType}/${itemId}/upload`,
        { method: 'POST', headers, body: formData }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Upload failed');
      }
      const data = await res.json();
      setItems(data.items || []);
      onCountChange?.((data.items || []).length);
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/file/${att.id}/download`,
        { headers }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Download failed');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.file_name}"?`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/file/${att.id}`,
        { method: 'DELETE', headers }
      );
      if (!res.ok) throw new Error('Delete failed');
      const next = items.filter((i) => i.id !== att.id);
      setItems(next);
      onCountChange?.(next.length);
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const count = items.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-slate-600 hover:text-slate-900"
          data-testid={`attachments-toggle-${itemType}-${itemId}`}
          title={`${count} attachment${count === 1 ? '' : 's'}`}
        >
          <Paperclip className="w-3.5 h-3.5 mr-1" />
          <span className="text-xs font-medium">{count}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-900">
            Attachments ({count})
          </h4>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid={`upload-input-${itemType}-${itemId}`}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              asChild
              disabled={uploading}
            >
              <span>
                {uploading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3 mr-1" />
                )}
                Upload
              </span>
            </Button>
          </label>
        </div>

        {loading ? (
          <div className="py-6 text-center text-xs text-slate-500">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">
            No attachments yet. Click <span className="font-semibold">Upload</span> to add files (multi-select supported, up to 50 MB each).
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-72 overflow-y-auto">
            {items.map((att) => (
              <li
                key={att.id}
                className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 rounded text-xs"
                data-testid={`attachment-row-${att.id}`}
              >
                <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <button
                  className="flex-1 text-left truncate hover:text-emerald-700"
                  onClick={() => handleDownload(att)}
                  title="Download"
                >
                  <span className="font-medium">{att.file_name}</span>
                  <span className="ml-1 text-slate-400">
                    ({formatBytes(att.file_size)})
                  </span>
                </button>
                <button
                  className="text-slate-400 hover:text-emerald-600"
                  onClick={() => handleDownload(att)}
                  title="Download"
                  data-testid={`download-${att.id}`}
                >
                  <Download className="w-3 h-3" />
                </button>
                <button
                  className="text-slate-400 hover:text-rose-600"
                  onClick={() => handleDelete(att)}
                  title="Delete"
                  data-testid={`delete-${att.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
